import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import {
    Button,
    Modal,
    FieldTextInput,
    FieldSelect,
    FieldCurrencyInput,
    FieldCheckboxGroup,
    Form,
    Heading,
} from "../../components";

import appSettings from '../../config/settings';
import { FormattedMessage } from '../../util/reactIntl';
import { supportedParnexusCurrencies } from '../../util/currency';
import {
    sellerRepresentationOptions,
    sellerProfileOptions,
    documentationOptions,
    proofAvailableOptions,
    sellerTimelineOptions,
    reasonForSaleOptions,
    mandateOptions,
    encumbrancesOptions,
    countryOptions,
} from '../../util/applicationFormsOptions';

import { getTransactionTypeData } from '../CheckoutPage/CheckoutPageTransactionHelpers';
import { getProcess } from '../../transactions/transaction';
import {
    required,
    requiredFieldArrayCheckbox,
    composeValidators,
    validateNoSensitiveInfoInMessage,
    validateInputField,
} from '../../util/validators';
import { pathByRouteName } from '../../util/routes';

import { useHistory } from 'react-router-dom';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import css from './SellerFormModal.module.css';

const SellerFormModal = (props) => {
    const {
        onClose,
        isOpen,
        onManageDisableScrolling,
        processName,
        listing,
        currentUser,
        config,
        initiateInquiryInProgress,
        initiateInquiryError,
        onInitiateinquiryWithApplicationForm,
    } = props;

    const history = useHistory();
    const routeConfiguration = useRouteConfiguration();

    const marketplaceCurrency = config.currency;
    const currencyConfig = appSettings.getCurrencyFormatting(marketplaceCurrency);

    // No transaction exists yet on this form, so the blocked-message warning
    // email is given the listing id to link to instead.
    const listingId = listing?.id?.uuid;

    const validateMessage = value =>
        validateNoSensitiveInfoInMessage(value, currentUser, undefined, listingId);

    const onSubmit = (formValues) => {
        const inquiryData = {
            representationStatus: formValues.representationStatus,
            sellerProfile: formValues.sellerProfile,
            documentation: formValues.documentation,
            proofAvailable: formValues.proofAvailable,
            sellerTimeline: formValues.sellerTimeline,
            reasonForSale: formValues.reasonForSale,
            mandate: formValues.mandate,
            encumbrances: formValues.encumbrances,
            countryLocation: formValues.countryLocation,
            currency: formValues.currency,
            askingPrice: formValues.askingPrice?.amount / 100,
            priceOnApplication: formValues.priceOnApplication,
            notes: formValues.notes,
            type: "seller-form",
        };

        const { listingType, transactionProcessAlias, unitType } = listing?.attributes?.publicData || {};

        const process = processName ? getProcess(processName) : null;
        const transitions = process.transitions;
        const transition = transitions.INQUIRE_WITHOUT_PAYMENT;
        const messageDate = new Date().getTime();

        const inquiryParams = {
            listingId: listing?.id,
            protectedData: {
                inquiryMessage: '',
                listingType: listingType,
                ...getTransactionTypeData(listingType, unitType, config),
            },
            metadata: {
                lastMessageSender: 'customer',
                lastMessageDate: messageDate,
                inquiryMessageDate: messageDate,
                providerHasUnreadMessages: true,
                customerAllowedToChat: false,
                sellerInquiryData: inquiryData,

                lastActionTime: messageDate,
                lastActionBy: 'customer',
                lastActionType: 'inquiry',
            },
        };

        onInitiateinquiryWithApplicationForm(inquiryParams, transactionProcessAlias, transition)
            .then(transactionId => {
                const orderDetailsPath = pathByRouteName('OrderDetailsPage', routeConfiguration, {
                    id: transactionId.uuid,
                });
                history.push(orderDetailsPath);
                onClose();
            })
            .catch(err => {
                console.error(err);
            });
    };

    return (
        <Modal
            id='SellerFormModal'
            onClose={onClose}
            closeButtonMessage={' '}
            containerClassName={css.sellerFormModal}
            usePortal
            isOpen={isOpen}
            onManageDisableScrolling={onManageDisableScrolling}
        >
            <div className={css.modalContent}>
                <Heading as="h4" className={css.Heading}>
                    <FormattedMessage id="SellerFormModal.Heading" />
                </Heading>
                <p className={css.info}>
                    <FormattedMessage id="SellerFormModal.info" />
                </p>
                <FinalForm
                    onSubmit={onSubmit}
                    mutators={{ ...arrayMutators }}
                    render={({ handleSubmit, pristine, invalid, values }) => {
                        const poa = values.priceOnApplication && values.priceOnApplication.length > 0;

                        return <Form onSubmit={handleSubmit}>
                            <div className={css.formField}>
                                <FieldSelect
                                    name="representationStatus"
                                    id="representationStatus"
                                    label="Representation status:"
                                    validate={required('Required')}
                                >
                                    <option disabled value="">Select an option</option>
                                    {sellerRepresentationOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </FieldSelect>
                            </div>

                            <div className={css.formField}>
                                <FieldSelect
                                    name="sellerProfile"
                                    id="sellerProfile"
                                    label="Profile of principal seller:"
                                    validate={required('Required')}
                                >
                                    <option disabled value="">Select an option</option>
                                    {sellerProfileOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </FieldSelect>
                            </div>

                            <div className={css.formField}>
                                <FieldCheckboxGroup
                                    id="documentation"
                                    name="documentation"
                                    label="Documentation and files available: (select all that apply)"
                                    options={documentationOptions.map(o => ({ key: o.value, label: o.label }))}
                                    validate={requiredFieldArrayCheckbox('Required')}
                                />
                            </div>

                            <div className={css.formField}>
                                <FieldCheckboxGroup
                                    id="proofAvailable"
                                    name="proofAvailable"
                                    label="Proof available: (select all that apply)"
                                    options={proofAvailableOptions.map(o => ({ key: o.value, label: o.label }))}
                                    validate={requiredFieldArrayCheckbox('Required')}
                                />
                            </div>

                            <div className={css.formField}>
                                <FieldSelect
                                    name="sellerTimeline"
                                    id="sellerTimeline"
                                    label="Seller timeline:"
                                    validate={required('Required')}
                                >
                                    <option disabled value="">Select an option</option>
                                    {sellerTimelineOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </FieldSelect>
                            </div>

                            <div className={css.formField}>
                                <FieldSelect
                                    name="reasonForSale"
                                    id="reasonForSale"
                                    label="Reason for sale: (optional)"
                                >
                                    <option value="">Select an option</option>
                                    {reasonForSaleOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </FieldSelect>
                            </div>

                            <div className={css.formField}>
                                <FieldSelect
                                    name="mandate"
                                    id="mandate"
                                    label="Existing mandate / exclusivity?"
                                    validate={required('Required')}
                                >
                                    <option disabled value="">Select an option</option>
                                    {mandateOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </FieldSelect>
                            </div>

                            <div className={css.formField}>
                                <FieldSelect
                                    name="encumbrances"
                                    id="encumbrances"
                                    label="Any encumbrances or liens?"
                                    validate={required('Required')}
                                >
                                    <option disabled value="">Select an option</option>
                                    {encumbrancesOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </FieldSelect>
                            </div>

                            <div className={css.formField}>
                                <FieldSelect
                                    name="countryLocation"
                                    id="countryLocation"
                                    label="Your location:"
                                    validate={required('Required')}
                                >
                                    <option disabled value="">Select a country</option>
                                    {countryOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </FieldSelect>
                            </div>

                            {poa ? null : <div className={css.curencyFields}>
                                <FieldSelect
                                    id="currency"
                                    name="currency"
                                    label="Currency"
                                    defaultValue="GBP"
                                    validate={required('Required')}
                                >
                                    <option disabled value="">Currency...</option>
                                    {supportedParnexusCurrencies.map(c => (
                                        <option key={c} value={c}>{c}</option>
                                    ))}
                                </FieldSelect>

                                <FieldCurrencyInput
                                    className={css.budgetField}
                                    name="askingPrice"
                                    id="askingPrice"
                                    label="Asking / guide price:"
                                    placeholder="Enter asking or guide price"
                                    currencyConfig={currencyConfig}
                                    validate={required('Required')}
                                    skipCurrencyDisplay={true}
                                />
                            </div>}

                            <div className={css.formField}>
                                <FieldCheckboxGroup
                                    id="priceOnApplication"
                                    name="priceOnApplication"
                                    options={[{ key: 'true', label: 'Price on application' }]}
                                />
                            </div>

                            <div className={css.formField}>
                                <FieldTextInput
                                    type="text"
                                    name="notes"
                                    id="notes"
                                    label="Additional notes: (optional)"
                                    placeholder="e.g. reason for sale, exclusivity terms, NDA requirements"
                                    validate={composeValidators(
                                        validateMessage,
                                        validateInputField(currentUser, undefined, listingId)
                                    )}
                                />
                            </div>

                            <div className={css.warningMessage}>
                                <FormattedMessage id="SellerFormModal.warningMessage" />
                            </div>

                            {initiateInquiryError ? <p>Failed to submit the form!</p> : null}

                            <div className={css.buttonContainer}>
                                <Button
                                    type="submit"
                                    disabled={pristine || invalid || initiateInquiryInProgress}
                                    inProgress={initiateInquiryInProgress}
                                    className={css.submitButton}
                                >
                                    <FormattedMessage id="SellerFormModal.submitFormButton" />
                                </Button>
                            </div>
                        </Form>
                    }}
                />
            </div>
        </Modal>
    );
};

export default SellerFormModal;
